import { useDraggable } from '@dnd-kit/core'
import { useState, useCallback } from 'react'
import { getAllBlocks, searchBlocks } from '@bst/blocks'
import type { BlockDefinition } from '@bst/blocks'
import type { BlockCategory } from '@bst/types'
import { useEditorStore } from '../../store/editor.store'
import { CustomBlocksPanel } from './CustomBlocksPanel'
import { SavedBlocksPanel } from './SavedBlocksPanel'
import { ComponentsPanel } from './ComponentsPanel'
import { DesignTokensPanel } from './DesignTokensPanel'

const RECENT_KEY = 'bst-recent-blocks'
const MAX_RECENT = 6

function getRecentBlocks(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[] } catch { return [] }
}
function trackRecentBlock(type: string) {
  const recent = getRecentBlocks().filter(t => t !== type)
  recent.unshift(type)
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

const CATEGORY_META: Record<BlockCategory, { label: string; emoji: string; order: number }> = {
  hero:        { label: 'Hero / Главный экран', emoji: '🏠', order: 1 },
  text:        { label: 'Текст и типографика',  emoji: 'T',  order: 2 },
  media:       { label: 'Медиа',                emoji: '🖼', order: 3 },
  gallery:     { label: 'Галерея',              emoji: '⊞',  order: 4 },
  slider:      { label: 'Слайдеры',             emoji: '▶◀', order: 5 },
  content:     { label: 'Контент',              emoji: '◈',  order: 6 },
  interactive: { label: 'Интерактив',           emoji: '⚡', order: 7 },
  form:        { label: 'Формы',                emoji: '✉',  order: 8 },
  navigation:  { label: 'Навигация',            emoji: '☰',  order: 9 },
  commerce:    { label: 'Магазин',              emoji: '💳', order: 10 },
  layout:      { label: 'Компоновка',           emoji: '⬜', order: 11 },
  embed:       { label: 'Встраивание',          emoji: '</>', order: 12 },
}

function DraggableBlockItem({ def, onUse }: { def: BlockDefinition; onUse?: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `library-${def.type}`,
    data: { type: 'new-block', blockType: def.type },
  })

  function handleClick() {
    const { page, selectedSectionId, addSection, addBlock } = useEditorStore.getState()
    const targetSection = selectedSectionId
      ? page.sections.find((s) => s.id === selectedSectionId)
      : page.sections[page.sections.length - 1]

    trackRecentBlock(def.type)
    onUse?.()

    if (targetSection) {
      addBlock(targetSection.id, def.type, def.defaultData)
    } else {
      addSection()
      setTimeout(() => {
        const { page: p, addBlock: ab } = useEditorStore.getState()
        const sec = p.sections[p.sections.length - 1]
        if (sec) ab(sec.id, def.type, def.defaultData)
      }, 0)
    }
  }

  return (
    <div
      ref={setNodeRef}
      className="block-item"
      style={{ opacity: isDragging ? 0.5 : 1 }}
      title={def.description}
      onClick={handleClick}
      {...listeners}
      {...attributes}
    >
      <span className="block-item-icon">{def.icon}</span>
      <span className="block-item-label">{def.label}</span>
    </div>
  )
}

function CategorySection({ category, blocks, defaultOpen, onUse }: { category: BlockCategory; blocks: BlockDefinition[]; defaultOpen: boolean; onUse?: () => void }) {
  const [open, setOpen] = useState(defaultOpen)
  const meta = CATEGORY_META[category]

  return (
    <div className="block-category">
      <button
        className="category-header"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '7px 12px',
          background: 'none',
          border: 'none',
          borderBottom: '1px solid var(--border)',
          cursor: 'pointer',
          gap: '7px',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '13px', minWidth: '16px', textAlign: 'center' }}>{meta?.emoji ?? '◈'}</span>
        <span style={{ flex: 1, fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {meta?.label ?? category}
        </span>
        <span style={{ fontSize: '10px', background: 'var(--bg-3)', color: 'var(--text-muted)', borderRadius: '10px', padding: '1px 6px', fontWeight: 600 }}>
          {blocks.length}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '2px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="block-items-grid" style={{ padding: '6px 8px 10px' }}>
          {blocks.map((def) => (
            <DraggableBlockItem key={def.type} def={def} onUse={onUse} />
          ))}
        </div>
      )}
    </div>
  )
}

export function BlockLibrary() {
  const [query, setQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'library' | 'custom' | 'saved' | 'components' | 'tokens'>('library')
  const hasProject = typeof window !== 'undefined' && !!localStorage.getItem('bst-project-id')
  const [recentTypes, setRecentTypes] = useState<string[]>(() => getRecentBlocks())
  const allBlocks = getAllBlocks()

  const refreshRecent = useCallback(() => setRecentTypes(getRecentBlocks()), [])

  const filtered = query.trim() ? searchBlocks(query) : allBlocks
  const recentDefs = recentTypes.map(t => allBlocks.find(b => b.type === t)).filter(Boolean) as BlockDefinition[]

  const byCategory = filtered.reduce<Partial<Record<BlockCategory, BlockDefinition[]>>>((acc, def) => {
    const cat = def.category as BlockCategory
    ;(acc[cat] ??= []).push(def)
    return acc
  }, {})

  const orderedCategories = (Object.keys(CATEGORY_META) as BlockCategory[])
    .sort((a, b) => (CATEGORY_META[a]?.order ?? 99) - (CATEGORY_META[b]?.order ?? 99))
    .filter((cat) => (byCategory[cat]?.length ?? 0) > 0)

  const tabBtn = (tab: 'library' | 'custom' | 'saved' | 'components' | 'tokens'): React.CSSProperties => ({
    flex: 1,
    padding: '6px',
    fontSize: '11px',
    fontWeight: activeTab === tab ? 700 : 500,
    background: activeTab === tab ? 'var(--accent)' : 'none',
    color: activeTab === tab ? '#fff' : 'var(--text-muted)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  })

  return (
    <div className="blocks-panel">
      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: '4px', padding: '8px 10px 0', background: 'var(--bg-1)' }}>
        <button style={tabBtn('library')} onClick={() => setActiveTab('library')}>Библиотека</button>
        <button style={tabBtn('custom')} onClick={() => setActiveTab('custom')}>🧩 Мои</button>
        <button style={tabBtn('saved')} onClick={() => setActiveTab('saved')}>📌</button>
        {hasProject && (
          <button style={tabBtn('components')} onClick={() => setActiveTab('components')}>★</button>
        )}
        {hasProject && (
          <button style={tabBtn('tokens')} onClick={() => setActiveTab('tokens')}>🎨</button>
        )}
      </div>

      {activeTab === 'custom' ? (
        <CustomBlocksPanel />
      ) : activeTab === 'saved' ? (
        <SavedBlocksPanel />
      ) : activeTab === 'components' ? (
        <ComponentsPanel />
      ) : activeTab === 'tokens' ? (
        <DesignTokensPanel />
      ) : (
        <>
          <div className="panel-search">
            <input
              type="text"
              placeholder="🔍 Поиск блоков..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px' }}
              >×</button>
            )}
          </div>

          {query.trim() && filtered.length > 0 && (
            <div style={{ padding: '6px 12px 4px', fontSize: '11px', color: 'var(--text-muted)' }}>
              Найдено: {filtered.length}
            </div>
          )}

          {!query.trim() && recentDefs.length > 0 && (
            <div className="block-category">
              <div style={{ display: 'flex', alignItems: 'center', padding: '7px 12px', borderBottom: '1px solid var(--border)', gap: '7px' }}>
                <span style={{ fontSize: '13px' }}>🕐</span>
                <span style={{ flex: 1, fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Недавние</span>
              </div>
              <div className="block-items-grid" style={{ padding: '6px 8px 10px' }}>
                {recentDefs.map(def => (
                  <DraggableBlockItem key={def.type} def={def} onUse={refreshRecent} />
                ))}
              </div>
            </div>
          )}

          <div className="block-categories">
            {orderedCategories.map((cat, idx) => (
              <CategorySection
                key={cat}
                category={cat}
                blocks={byCategory[cat]!}
                defaultOpen={idx < 2 || !!query.trim()}
                onUse={refreshRecent}
              />
            ))}
            {filtered.length === 0 && (
              <div className="blocks-empty" style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                Ничего не найдено по запросу «{query}»
              </div>
            )}
          </div>

          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
            {allBlocks.length} блоков · нажмите или перетащите
          </div>
        </>
      )}
    </div>
  )
}
